import { motion } from 'framer-motion';
import { Check, X, Zap, Crown, Star, MessageCircle, FileText, Clock, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const planColors = ['#94A3B8', '#38BDF8', '#FCD34D', '#5EEAD4'];

export const SubscriptionPage = () => {
  const { t } = useLanguage();

  const plans = [
    {
      id: 'free',
      name: 'Free',
      description: t('subscription.freeDesc'),
      icon: Star,
      gradient: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
      accentColor: '#94A3B8',
      glowColor: 'rgba(148,163,184,0.12)',
      borderColor: 'rgba(148,163,184,0.2)',
      features: [
        { text: t('subscription.feat5msg'), included: true },
        { text: t('subscription.feat3posts'), included: true },
        { text: t('subscription.featPush'), included: true },
        { text: t('subscription.featHashtag'), included: true },
        { text: t('subscription.featMatrix'), included: true },
        { text: t('subscription.featVoice'), included: false },
        { text: t('subscription.featVideo'), included: false },
        { text: t('subscription.featTranslation'), included: false },
        { text: t('subscription.featReducedAds'), included: false },
        { text: t('subscription.featAdFree'), included: false },
      ],
    },
    {
      id: 'plus',
      name: 'Plus',
      description: t('subscription.plusDesc'),
      icon: Zap,
      gradient: 'linear-gradient(135deg, #0C4A6E 0%, #0369A1 100%)',
      accentColor: '#38BDF8',
      glowColor: 'rgba(56,189,248,0.18)',
      borderColor: 'rgba(56,189,248,0.3)',
      features: [
        { text: t('subscription.feat15msg'), included: true },
        { text: t('subscription.feat7posts'), included: true },
        { text: t('subscription.featPush'), included: true },
        { text: t('subscription.featHashtag'), included: true },
        { text: t('subscription.featMatrix'), included: true },
        { text: t('subscription.featVoice'), included: true },
        { text: t('subscription.featReducedAds'), included: true },
        { text: t('subscription.featPacks'), included: true },
        { text: t('subscription.featVideo'), included: false },
        { text: t('subscription.featAdFree'), included: false },
        { text: t('subscription.featTranslation'), included: false },
      ],
    },
    {
      id: 'gold',
      name: 'Gold',
      description: t('subscription.goldDesc'),
      icon: Star,
      gradient: 'linear-gradient(135deg, #78350F 0%, #D97706 100%)',
      accentColor: '#FCD34D',
      glowColor: 'rgba(252,211,77,0.2)',
      borderColor: 'rgba(252,211,77,0.35)',
      popular: true,
      features: [
        { text: t('subscription.feat30msg'), included: true },
        { text: t('subscription.feat15posts'), included: true },
        { text: t('subscription.featPush'), included: true },
        { text: t('subscription.featHashtag'), included: true },
        { text: t('subscription.featMatrix'), included: true },
        { text: t('subscription.featVoice'), included: true },
        { text: t('subscription.featVideo'), included: true },
        { text: t('subscription.featCompleteAdFree'), included: true },
        { text: t('subscription.featPacks'), included: true },
        { text: t('subscription.featTranslation'), included: false },
      ],
    },
    {
      id: 'premium',
      name: 'Premium',
      description: t('subscription.premiumDesc'),
      icon: Crown,
      gradient: 'linear-gradient(135deg, #004D40 0%, #26A69A 100%)',
      accentColor: '#5EEAD4',
      glowColor: 'rgba(94,234,212,0.2)',
      borderColor: 'rgba(94,234,212,0.35)',
      features: [
        { text: t('subscription.featUnlimitedMsg'), included: true },
        { text: t('subscription.featUnlimitedPosts'), included: true },
        { text: t('subscription.featPush'), included: true },
        { text: t('subscription.featHashtag'), included: true },
        { text: t('subscription.featMatrix'), included: true },
        { text: t('subscription.featVoice'), included: true },
        { text: t('subscription.featVideo'), included: true },
        { text: t('subscription.featCompleteAdFree'), included: true },
        { text: t('subscription.featTranslation'), included: true },
        { text: t('subscription.featExtendedMatch'), included: true },
      ],
    },
  ];

  const durations = [
    { label: t('subscription.monthly'), tag: null },
    { label: t('subscription.months3'), tag: t('subscription.popularTag') },
    { label: t('subscription.months6'), tag: null },
    { label: t('subscription.months12'), tag: t('subscription.bestValueTag') },
  ];

  const packs = [
    {
      icon: MessageCircle,
      title: t('subscription.msgPackTitle'),
      color: '#38BDF8',
      description: t('subscription.msgPackDesc'),
      options: [t('subscription.packSmallMsg'), t('subscription.packMediumMsg'), t('subscription.packLargeMsg')],
    },
    {
      icon: FileText,
      title: t('subscription.postPackTitle'),
      color: '#FCD34D',
      description: t('subscription.postPackDesc'),
      options: [t('subscription.packSmallPost'), t('subscription.packMediumPost'), t('subscription.packLargePost')],
    },
    {
      icon: Clock,
      title: t('subscription.callPackTitle'),
      color: '#5EEAD4',
      description: t('subscription.callPackDesc'),
      options: [t('subscription.packSmallCall'), t('subscription.packMediumCall'), t('subscription.packLargeCall'), t('subscription.packXLCall')],
    },
  ];

  const comparisonRows = [
    { feature: t('subscription.cmpDailyMsg'), values: ['5', '15', '30', '∞'] },
    { feature: t('subscription.cmpDailyPosts'), values: ['3', '7', '15', '∞'] },
    { feature: t('subscription.cmpPush'), values: [true, true, true, true] },
    { feature: t('subscription.cmpVoice'), values: [false, true, true, true] },
    { feature: t('subscription.cmpVideo'), values: [false, false, true, true] },
    { feature: t('subscription.cmpTranslation'), values: [false, false, false, true] },
    { feature: t('subscription.cmpAdFree'), values: [false, t('subscription.partial'), true, true] },
    { feature: t('subscription.cmpMatchWindow'), values: [false, false, false, true] },
    { feature: t('subscription.cmpPacks'), values: [false, true, true, true] },
  ];

  return (
    <div className="min-h-screen bg-[#080B0F] text-white">
      <div className="h-20" />

      {/* Hero */}
      <section className="relative overflow-hidden pt-16 pb-12 px-4">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] bg-[#008080]/8 blur-[120px] rounded-full" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <Crown className="w-4 h-4 text-[#FCD34D]" />
              <span className="font-poppins text-sm text-white/70">{t('subscription.badge')}</span>
            </div>
            <h1 className="font-montserrat font-black text-4xl md:text-6xl text-white mb-4 leading-tight">
              {t('subscription.heroTitle')}{' '}
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #008080, #5EEAD4)' }}>
                {t('subscription.heroPlan')}
              </span>
            </h1>
            <p className="font-poppins text-white/60 text-lg max-w-2xl mx-auto leading-relaxed">
              {t('subscription.heroDesc')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Billing Durations */}
      <section className="px-4 pb-6 max-w-6xl mx-auto">
        <div className="flex flex-wrap justify-center gap-3">
          {durations.map((d) => (
            <div key={d.label} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
              <span className="font-poppins text-sm text-white/70">{d.label}</span>
              {d.tag && (
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#008080]/30 text-[#5EEAD4]">
                  {d.tag}
                </span>
              )}
            </div>
          ))}
        </div>
        <p className="text-center text-white/30 text-xs font-poppins mt-3">
          {t('subscription.billingNote')}
        </p>
      </section>

      {/* Plan Cards */}
      <section className="px-4 pb-16 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((plan, index) => {
            const Icon = plan.icon;
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="relative rounded-3xl overflow-hidden flex flex-col"
                style={{
                  border: `1px solid ${plan.borderColor}`,
                  boxShadow: `0 0 40px ${plan.glowColor}`,
                }}
              >
                {plan.popular && (
                  <div className="absolute top-3 right-3 z-10 flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FCD34D] text-[#78350F]">
                    <Star className="w-3 h-3" />
                    {t('subscription.mostPopular')}
                  </div>
                )}

                <div className="absolute inset-0" style={{ background: plan.gradient }} />

                <div className="relative z-10 p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${plan.accentColor}25` }}>
                      <Icon className="w-5 h-5" style={{ color: plan.accentColor }} />
                    </div>
                    <div>
                      <h3 className="font-montserrat font-bold text-lg text-white">{plan.name}</h3>
                    </div>
                  </div>

                  <p className="text-white/50 text-xs font-poppins leading-relaxed mb-4 pb-4 border-b border-white/10">
                    {plan.description}
                  </p>

                  <ul className="space-y-2.5 flex-1">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2">
                        {feature.included ? (
                          <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${plan.accentColor}30` }}>
                            <Check className="w-2.5 h-2.5" style={{ color: plan.accentColor }} />
                          </div>
                        ) : (
                          <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 bg-white/5">
                            <X className="w-2.5 h-2.5 text-white/20" />
                          </div>
                        )}
                        <span className={`text-xs font-poppins ${feature.included ? 'text-white/80' : 'text-white/25'}`}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-1.5 text-white/40 text-xs font-poppins">
                    <Download className="w-3.5 h-3.5" />
                    <span>{t('subscription.purchaseInApp')}</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="px-4 pb-16 max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <h2 className="font-montserrat font-bold text-3xl text-white mb-2">{t('subscription.comparisonTitle')}</h2>
          <p className="text-white/50 font-poppins text-sm">{t('subscription.comparisonDesc')}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="rounded-2xl overflow-hidden border border-white/10"
        >
          {/* Table header */}
          <div className="grid grid-cols-5 bg-white/5">
            <div className="p-4 font-montserrat font-semibold text-white/50 text-sm">{t('subscription.featureLabel')}</div>
            {plans.map((plan) => (
              <div key={plan.id} className="p-4 text-center font-montserrat font-bold text-sm" style={{ color: plan.accentColor }}>
                {plan.name}
              </div>
            ))}
          </div>

          {/* Table rows */}
          {comparisonRows.map((row, i) => (
            <div
              key={i}
              className={`grid grid-cols-5 border-t border-white/5 ${i % 2 === 0 ? 'bg-white/2' : 'bg-transparent'}`}
            >
              <div className="p-3.5 pl-4 font-poppins text-white/60 text-xs flex items-center">{row.feature}</div>
              {row.values.map((val, j) => (
                <div key={j} className="p-3.5 flex items-center justify-center">
                  {val === true ? (
                    <Check className="w-4 h-4" style={{ color: planColors[j] }} />
                  ) : val === false ? (
                    <X className="w-4 h-4 text-white/15" />
                  ) : (
                    <span className="text-xs font-poppins font-semibold" style={{ color: planColors[j] }}>{val}</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </motion.div>
      </section>

      {/* Add-On Packs */}
      <section className="px-4 pb-16 max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <h2 className="font-montserrat font-bold text-3xl text-white mb-2">{t('subscription.addOnTitle')}</h2>
          <p className="text-white/50 font-poppins text-sm">{t('subscription.addOnDesc')}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {packs.map((pack, index) => {
            const Icon = pack.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-5 rounded-2xl bg-white/5 border border-white/10"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${pack.color}20` }}>
                    <Icon className="w-5 h-5" style={{ color: pack.color }} />
                  </div>
                  <h3 className="font-montserrat font-semibold text-white">{pack.title}</h3>
                </div>
                <p className="text-white/50 text-xs font-poppins leading-relaxed mb-3">{pack.description}</p>
                <ul className="space-y-1.5">
                  {pack.options.map((opt, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs font-poppins text-white/60">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: pack.color }} />
                      {opt}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 pb-24 max-w-2xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-8 rounded-3xl bg-gradient-to-br from-[#008080]/20 to-[#004D4D]/10 border border-[#008080]/30"
        >
          <h2 className="font-montserrat font-bold text-2xl text-white mb-2">
            {t('subscription.ctaTitle')}
          </h2>
          <p className="text-white/50 font-poppins text-sm mb-6 leading-relaxed">
            {t('subscription.ctaDesc')}
          </p>
          <p className="text-white/30 font-poppins text-xs mb-6">
            {t('subscription.ctaNote')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              className="px-6 py-3 rounded-full font-poppins font-semibold text-white text-sm transition-all"
              style={{ background: 'linear-gradient(135deg, #008080, #004D4D)', boxShadow: '0 0 20px rgba(0,128,128,0.3)' }}
            >
              {t('subscription.downloadApp')}
            </Link>
            <Link
              to="/contact-us"
              className="px-6 py-3 rounded-full font-poppins font-semibold text-white/70 hover:text-white text-sm border border-white/20 hover:border-white/40 transition-colors"
            >
              {t('subscription.haveQuestions')}
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};
